/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PageUrlSection — Accordion 1: slug input + live URL preview + uniqueness check.
 *
 * Slug editing behaviour after first save:
 *   • The slug is shown as read-only (locked) to prevent accidental changes.
 *   • The user must explicitly click "Change Slug" to unlock editing.
 *   • While unlocked a prominent danger banner is shown explaining the risk.
 *   • "Revert" restores the original saved slug and re-locks the field.
 *   • Saving is still allowed (per spec) — the intent is deliberate, not blocked.
 */

import React, { useState } from 'react';
import { useSlugAvailability } from '../../../hooks/useSlugAvailability';

export default function PageUrlSection({ tableId, config, updateConfig, theme }) {
  const savedSlug  = config.table_slug || '';          // slug already persisted in DB
  const [slugInput, setSlugInput] = useState(config.slug || savedSlug);
  const [unlocked, setUnlocked]   = useState(false);  // user must explicitly unlock

  const { available, checking, usedBy } = useSlugAvailability(tableId, slugInput);

  const publicBase          = process.env.REACT_APP_PUBLIC_CLIENT_URL || window.location.origin;
  const isLiveAndChanged    = !!savedSlug && slugInput !== savedSlug;

  function handleChange(e) {
    setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'));
  }

  function handleBlur() {
    const trimmed = slugInput.trim();
    if (trimmed !== config.slug) updateConfig({ slug: trimmed });
  }

  function handleUnlock() {
    setUnlocked(true);
  }

  function handleRevert() {
    setSlugInput(savedSlug);
    updateConfig({ slug: savedSlug });
    setUnlocked(false);
  }

  // ── Locked view — shown when a slug is already saved and user hasn't clicked "Change" ──
  if (savedSlug && !unlocked) {
    return (
      <div>
        <label className="form-label fw-semibold">URL Slug</label>

        {/* Read-only display */}
        <div
          className="d-flex align-items-center gap-2 px-3 py-2 rounded mb-2"
          style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
        >
          <i className="bi bi-lock-fill text-muted" style={{ fontSize: 13 }} />
          <code style={{ fontSize: 13, flex: 1, color: theme.primary }}>
            /p/{savedSlug}
          </code>
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleUnlock}
            style={{
              fontSize: 12,
              padding: '2px 10px',
              borderRadius: 6,
              border: `1px solid ${theme.primary}50`,
              color: theme.primary,
              background: 'transparent',
              fontWeight: 600,
            }}
          >
            <i className="bi bi-pencil me-1" style={{ fontSize: 11 }} />
            Change Slug
          </button>
        </div>

        <div className="form-text">
          This slug is live at{' '}
          <a
            href={`${publicBase}/p/${savedSlug}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: theme.primary }}
          >
            {publicBase}/p/{savedSlug}
          </a>
          . Click <strong>Change Slug</strong> to modify it.
        </div>
      </div>
    );
  }

  // ── Unlocked / new slug view ────────────────────────────────────────────────
  return (
    <div>
      <label className="form-label fw-semibold">URL Slug</label>

      <div className="input-group mb-1">
        <span className="input-group-text text-muted" style={{ fontSize: 13 }}>/p/</span>
        <input
          type="text"
          className={`form-control ${
            available === false ? 'is-invalid' : available === true && slugInput ? 'is-valid' : ''
          }`}
          value={slugInput}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="my-table-slug"
          autoFocus={!!savedSlug}
        />
        {checking && (
          <span className="input-group-text">
            <span className="spinner-border spinner-border-sm text-muted" />
          </span>
        )}
        {/* Revert button — only when editing an existing live slug */}
        {savedSlug && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleRevert}
            title={`Revert to /p/${savedSlug}`}
            style={{ fontSize: 12, borderRadius: '0 6px 6px 0' }}
          >
            <i className="bi bi-arrow-counterclockwise me-1" />
            Revert
          </button>
        )}
      </div>

      {/* Availability feedback */}
      {available === true && slugInput && !isLiveAndChanged && (
        <div className="valid-feedback d-block small">
          <i className="bi bi-check-circle me-1 text-success" /> Slug is available.
        </div>
      )}
      {available === false && (
        <div className="invalid-feedback d-block small">
          <i className="bi bi-x-circle me-1 text-danger" />
          Slug already in use{usedBy ? ` by "${usedBy}"` : ''}.
        </div>
      )}

      {/* Prominent danger banner — only when changing an already-live slug */}
      {isLiveAndChanged && (
        <div
          className="mt-2 p-3 rounded"
          style={{ background: '#fff1f2', border: '1.5px solid #fca5a5' }}
        >
          <div className="d-flex align-items-start gap-2 mb-2">
            <i className="bi bi-exclamation-octagon-fill text-danger mt-1" style={{ flexShrink: 0, fontSize: 15 }} />
            <div className="small" style={{ color: '#991b1b' }}>
              <strong>Breaking change.</strong> Saving will permanently move this page from{' '}
              <code style={{ fontSize: 11, background: '#fee2e2', padding: '1px 5px', borderRadius: 4 }}>
                /p/{savedSlug}
              </code>{' '}
              to{' '}
              <code style={{ fontSize: 11, background: '#fee2e2', padding: '1px 5px', borderRadius: 4 }}>
                /p/{slugInput || '…'}
              </code>
              . Any existing bookmarks, shared links, or integrations pointing to the old URL will stop working.
            </div>
          </div>
          <button
            type="button"
            onClick={handleRevert}
            className="btn btn-sm"
            style={{
              fontSize: 12,
              padding: '3px 12px',
              border: '1px solid #f87171',
              color: '#991b1b',
              background: '#fff',
              borderRadius: 6,
              fontWeight: 600,
            }}
          >
            <i className="bi bi-arrow-counterclockwise me-1" />
            Keep /p/{savedSlug}
          </button>
        </div>
      )}

      <div className="form-text mt-1">
        Only lowercase letters, numbers, hyphens and underscores allowed.
        {!savedSlug && ' The slug can be updated later, but changing a live slug breaks existing shared links.'}
      </div>

      {/* Live URL preview */}
      <div className="p-2 rounded mt-2" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div className="small text-muted mb-1">Preview URL</div>
        <code style={{ fontSize: 13, wordBreak: 'break-all', color: theme.primary }}>
          {publicBase}/p/{slugInput || '<slug>'}
        </code>
      </div>
    </div>
  );
}
